import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import type { Product } from '../data/products';
export type CartLine = {
  product: Product;
  qty: number;
};
type StoreValue = {
  lines: CartLine[];
  count: number;
  subtotal: number;
  freeShippingThreshold: number;
  add: (product: Product) => void;
  setQty: (id: string, qty: number) => void;
  remove: (id: string) => void;
  cartOpen: boolean;
  openCart: () => void;
  closeCart: () => void;
  bookingTreatment: string | null;
  bookingOpen: boolean;
  openBooking: (treatment?: string) => void;
  closeBooking: () => void;
};
const StoreContext = createContext<StoreValue | null>(null);
export function StoreProvider({
  children
}: {
  children: React.ReactNode;
}) {
  const [lines, setLines] = useState<CartLine[]>([]);
  const [cartOpen, setCartOpen] = useState(false);
  const [bookingOpen, setBookingOpen] = useState(false);
  const [bookingTreatment, setBookingTreatment] = useState<string | null>(null);
  const add = useCallback((product: Product) => {
    setLines(prev => {
      const existing = prev.find(l => l.product.id === product.id);
      if (existing) {
        return prev.map(l => l.product.id === product.id ? {
          ...l,
          qty: l.qty + 1
        } : l);
      }
      return [...prev, {
        product,
        qty: 1
      }];
    });
    setCartOpen(true);
  }, []);
  const setQty = useCallback((id: string, qty: number) => {
    setLines(prev => qty <= 0 ? prev.filter(l => l.product.id !== id) : prev.map(l => l.product.id === id ? {
      ...l,
      qty
    } : l));
  }, []);
  const remove = useCallback((id: string) => {
    setLines(prev => prev.filter(l => l.product.id !== id));
  }, []);
  const openBooking = useCallback((treatment?: string) => {
    setBookingTreatment(treatment ?? null);
    setBookingOpen(true);
  }, []);
  const value = useMemo<StoreValue>(() => {
    const count = lines.reduce((n, l) => n + l.qty, 0);
    const subtotal = lines.reduce((n, l) => n + l.qty * l.product.price, 0);
    return {
      lines,
      count,
      subtotal,
      freeShippingThreshold: 100,
      add,
      setQty,
      remove,
      cartOpen,
      openCart: () => setCartOpen(true),
      closeCart: () => setCartOpen(false),
      bookingTreatment,
      bookingOpen,
      openBooking,
      closeBooking: () => setBookingOpen(false)
    };
  }, [lines, cartOpen, bookingOpen, bookingTreatment, add, setQty, remove, openBooking]);
  return <StoreContext.Provider value={value} data-id="element-263" data-mp="39n1tafx3hpi9">{children}</StoreContext.Provider>;
}
export function useStore() {
  const ctx = useContext(StoreContext);
  if (!ctx) throw new Error('useStore must be used within StoreProvider');
  return ctx;
}